// File: SamudraPage.jsx
import React, { useState, useEffect } from "react";
import "./claimspage.css";
import Footer from "./Footer";
import Toast from "./Toast";
import { API_BASE_URL } from '../config';

export default function ClaimsPage() {
  // Feedback rating (replaces Alpine.js)
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);

  // Toast popup state
  const [toast, setToast] = useState({ visible: false, message: "", type: "info" });

  function showToast(message, type = "info") {
    setToast({ visible: true, message, type });
    setTimeout(() => setToast((t) => ({ ...t, visible: false })), 3500);
  }

  // Claim status display
  const [claimRef, setClaimRef] = useState("");
  const [statusVisible, setStatusVisible] = useState(false);
  const [statusText, setStatusText] = useState("");
  const [statusBorderColor, setStatusBorderColor] = useState("gray");

  useEffect(() => {
    // mimic "default tab active" logic from original
    // (kept for parity if tabs are added later)
  }, []);

  // Helper to build JSON from a form element
  function getFormData(form) {
    const data = {};
    new FormData(form).forEach((value, key) => (data[key] = value));
    return data;
  }

  // Basic validators
  function isValidEmail(email) {
    return typeof email === "string" && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  function validateInquiryData(data) {
    if (!data.claim_type) return "Please select a Claim Type.";
    if (!data.email) return "Please provide your professional email.";
    if (!isValidEmail(data.email)) return "Please provide a valid email address.";
    if (!data.description || data.description.trim().length < 10) return "Please provide a brief description (at least 10 characters).";
    return null;
  }

  function validateFeedbackData(data) {
    // Email is required
    if (!data.feedback_email) return "Please provide your professional email for feedback.";
    if (!isValidEmail(data.feedback_email)) return "Please provide a valid email address for feedback.";

    const hasText = data.feedback_text && data.feedback_text.trim().length > 3;
    const hasRating = typeof data.satisfaction_rating === "number" ? data.satisfaction_rating > 0 : !!data.satisfaction_rating;
    if (!hasText && !hasRating) return "Please provide feedback text or select a rating.";
    return null;
  }

  function validateSubscribeData(data) {
    if (!data.newsletter_email) return "Please provide an email to subscribe.";
    if (!isValidEmail(data.newsletter_email)) return "Please provide a valid email address.";
    return null;
  }

  // function validateClaimRef(ref) {
  //   if (!ref || ref.trim().length === 0) return "Please enter a claim reference number.";
  //   if (ref.trim().length < 5) return "Claim reference looks too short. Check and try again.";
  //   return null;
  // }

  async function submitInquiry(e) {
    e.preventDefault();
    const form = e.target;
    const data = getFormData(form);
    const validationError = validateInquiryData(data);
    if (validationError) {
      showToast(validationError, "error");
      return;
    }

    try {
      const resp = await fetch(
        `${API_BASE_URL}/claims`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(data),
        }
      );

      if (resp.ok) {
        showToast("✅ Inquiry Submitted Successfully! We will get back to you soon.", "success");
        form.reset();
      } else {
        const err = await resp.json().catch(() => ({ message: "Server error occurred." }));
        showToast(`❌ Submission Failed: ${err.message || "Server error occurred."}`, "error");
      }
    } catch (err) {
      console.error("Network error during inquiry submission:", err);
      showToast("A network error occurred. Please try again or contact us directly.", "error");
    }
  }

  async function submitFeedback(e) {
    e.preventDefault();
    const form = e.target;
    const data = getFormData(form);
    data.satisfaction_rating = rating;

    const validationError = validateFeedbackData(data);
    if (validationError) {
      showToast(validationError, "error");
      return;
    }

    try {
      const resp = await fetch(
        `${API_BASE_URL}/feedback`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(data),
        }
      );

      if (resp.ok) {
        showToast("🌟 Feedback submitted! Thank you for helping us achieve Continuous Improvement.", "success");
        form.reset();
        setRating(0);
      } else {
        showToast("❌ Feedback submission failed.", "error");
      }
    } catch (err) {
      console.error("Network error during feedback submission:", err);
      showToast("A network error occurred while submitting feedback.", "error");
    }
  }

  async function subscribeNewsletter(e) {
    e.preventDefault();
    const form = e.target;
    const data = getFormData(form);
    const validationError = validateSubscribeData(data);
    if (validationError) {
      showToast(validationError, "error");
      return;
    }

    // optimistic success for demo / offline API
    showToast("🚢 Subscribed to Maritime Insights! Check your email for confirmation.", "success");

    // try {
    //   const resp = await fetch("https://api.samudra.com/subscribe", {
    //     method: "POST",
    //     headers: { "Content-Type": "application/json" },
    //     body: JSON.stringify(data),
    //   });

    //   if (resp.ok) {
    //     showToast("🚢 Subscribed to Maritime Insights! Check your email for confirmation.", "success");
    //     form.reset();
    //   } else {
    //     showToast("❌ Subscription failed. Please check your email address.", "error");
    //   }
    // } catch (err) {
    //   console.error("Network error during subscription:", err);
    //   showToast("A network error occurred. Please try again.", "error");
    // }
  }

  async function checkClaimStatus(e) {
    e && e.preventDefault();
    const ref = claimRef.trim().toUpperCase();
    setStatusVisible(true);
    setStatusText("Looking up claim status...");
    setStatusBorderColor("gray");

    try {
      const resp = await fetch(
        `${API_BASE_URL}/get-claim/${ref}`,
        {
          method: "GET",
          headers: { Accept: "application/json" },
        }
      );

      if (resp.ok) {
        const data = await resp.json();
        let text = data.status || "Status not available.";
        let color = "green";

        // if (data.isFinalized) {
        //   color = "green";
        //   text = `Claim Finalized (${data.claimType || "N/A"}). Adjustment report issued to Underwriters on ${data.reportDate || "N/A"}. Settlement instructions pending.`;
        // } else if (data.isAwaitingDocs) {
        //   color = "red";
        //   text = `Status On Hold. Awaiting essential documents: ${data.awaitingDocuments || "details required"}. Please submit ASAP to resume adjustment.`;
        // } else if (data.isProcessing) {
        //   color = "orange";
        //   text = `Status Processing. Adjuster assigned and calculating ${data.processType || "N/A"}. Expected completion: ${data.expectedDate || "N/A"}.`;
        // } else if (data.isLogged) {
        //   color = "blue";
        //   text = `Inquiry Logged. Initial assessment in progress. Details: ${data.details || "We will contact you shortly."}`;
        // }

        setStatusBorderColor(color);
        setStatusText(text);
      } else {
        if (resp.status === 404) {
          setStatusBorderColor("gray");
          setStatusText(`Reference ${ref} not found or is invalid. Please check the number or use the Inquiry Form to contact us immediately.`);
        } else {
          setStatusBorderColor("red");
          setStatusText("An unexpected server error occurred while retrieving status.");
        }
      }
    } catch (err) {
      console.error("Network error during status lookup:", err);
      setStatusBorderColor("red");
      setStatusText("A network error occurred. Cannot connect to the status service.");
    }
  }

  return (
    <div className="page-root">
      <header className="hero">
        <a className="home-link" href="./" aria-label="Home">
          <svg className="home-icon" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
            <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
          </svg>
          Home
        </a>

        <div className="hero-content" id="new-claims">
          <h1>Marine Claims & Adjusting</h1>
        </div>
      </header>

      <main>
        <section className="inquiry-section">
          <div className="container">
            <div className="left">
              <div className="urgent-banner">
                <span className="urgent-text">CRITICAL INCIDENT? SUBMIT URGENT CLAIM HERE</span>
              </div>

              <h2 className="section-title">Service Inquiry / Incident Report</h2>

              <form className="inquiry-form" onSubmit={submitInquiry}>
                <div className="form-row two-cols">
                  <div className="field">
                    <label>Claim Type* (Required)</label>
                    <select name="claim_type" required>
                      <option value="">-- Select Claim/Service Type --</option>
                      <option>Average Adjusting (GA/PA)</option>
                      <option>Cargo Claim</option>
                      <option>Collision Liability</option>
                      <option>Vessel Inspection/Audit</option>
                      <option>General Feedback / Other</option>
                    </select>
                  </div>

                  <div className="field">
                    <label>Vessel Name / Reference</label>
                    <input type="text" name="vessel_name" placeholder="Vessel Name or Claim File #" />
                  </div>
                </div>

                <div className="form-row two-cols">
                  <div className="field">
                    <label>IMO Number / Policy Ref</label>
                    <input type="text" name="imo_number" placeholder="Optional reference number" />
                  </div>
                  <div className="field">
                    <label>Incident Date</label>
                    <input type="date" name="incident_date" />
                  </div>
                </div>

                <div className="field">
                  <label>Your Professional Email*</label>
                  <input type="email" name="email" required placeholder="name@company.com" />
                </div>

                <div className="field">
                  <label>Brief Description of Incident / Needs*</label>
                  <textarea name="description" rows={5} required placeholder="Describe the incident, location, immediate actions taken, and urgency..."></textarea>
                </div>

                {/* <div className="captcha-placeholder">[CAPTCHA/ReCAPTCHA Security Check Placeholder]</div>
                <p className="small-muted">Required for submission. Your data is handled with strict PDPA/GDPR compliance for confidentiality.</p> */}

                <button type="submit" className="btn-secondary full">SUBMIT SERVICE INQUIRY</button>
              </form>
            </div>

            <aside className="right">
              <div className="client-hub">
                <h3>🌟 Client Feedback Form</h3>
                <p className="small-muted">Help us achieve Continuous Improvement. Your insights matter.</p>
                <form onSubmit={submitFeedback} className="feedback-form">
                  <div className="field">
                      <label className="small">Your Professional Email*</label>
                      <input type="email" name="feedback_email" required placeholder="name@company.com" />
                  </div>

                  <div className="field">
                    <label className="small">Your Feedback/Suggestion</label>
                    <textarea name="feedback_text" rows={3} placeholder="Insights on our service delivery, speed, or reports..."></textarea>
                  </div>

                  <div className="rating-row">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <svg
                        key={i}
                        className={`star ${i <= (hoverRating || rating) ? "active" : ""}`}
                        viewBox="0 0 20 20"
                        onClick={() => setRating(i)}
                        onMouseEnter={() => setHoverRating(i)}
                        onMouseLeave={() => setHoverRating(0)}
                      >
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.538 1.118l-2.8-2.034a1 1 0 00-1.176 0l-2.8 2.034c-.783.57-1.838-.197-1.538-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.381-1.81.588-1.81h3.462a1 1 0 00.95-.69l1.07-3.292z" />
                      </svg>
                    ))}
                  </div>

                  <button type="submit" className="btn-primary full">Send Feedback</button>
                </form>
              </div>

              <div className="newsletter">
                <h3>🚢 Subscribe to Maritime Insights</h3>
                <p className="small-muted">Receive expert commentary on changes in marine insurance law and claims.</p>
                <form className="subscribe-form" onSubmit={subscribeNewsletter}>
                  <input type="email" name="newsletter_email" required placeholder="Professional Email" />
                  <button type="submit" className="btn-secondary">Subscribe</button>
                </form>
              </div>
            </aside>
          </div>
        </section>

        <section id="status-lookup" className="status-lookup">
          <div className="status-container">
            <h2>🚢 Track Your Claim Progress</h2>
            <p className="small-muted">Enter your unique claim reference number (e.g., SA-2025-0045) to view the latest adjustment status.</p>

            <form className="status-form" onSubmit={checkClaimStatus}>
              <input
                type="text"
                value={claimRef}
                onChange={(e) => setClaimRef(e.target.value)}
                placeholder="Enter Claim Reference (e.g., SA-2025-0045)"
                required
              />
              <button className="btn-secondary" type="submit">Check Status</button>
            </form>

            {statusVisible && (
              <div className="status-box" style={{ borderColor: statusBorderColor }}>
                <p className="status-label">Status:</p>
                <p className="status-text">{statusText}</p>
              </div>
            )}
          </div>
        </section>
      </main>

      {/* <footer className="site-footer">
        <div className="footer-grid">
          <div>
            <div className="brand">SAMUDRA</div>
            <p>Expertise, precision, and reliability in marine claims and consultancy. Registered in Singapore.</p>
          </div>

          <div>
            <h4>Quick Links</h4>
            <ul>
              <li><a href="#services">Our Services</a></li>
              <li><a href="#claims-center">Claims Center</a></li>
              <li><a href="#newsroom">Newsroom</a></li>
              <li><a href="#resources">Case Studies & Insights</a></li>
            </ul>
          </div>

          <div>
            <h4>Compliance & Legal</h4>
            <ul>
              <li><a href="#">Privacy Policy (PDPA)</a></li>
              <li><a href="#">Terms of Service</a></li>
              <li><a href="#">Sitemap</a></li>
            </ul>
          </div>

          <div>
            <h4>Contact</h4>
            <p>Singapore Headquarters</p>
            <p>Email: claims@samudra.example</p>
            <p>24/7 Phone: +65 6123 4567</p>
          </div>
        </div>

        <div className="copyright">© 2025 Samudra Adjusting & Marine Insurance Solutions Pte Ltd. Document Version 1.0.</div>
      </footer> */}
      <Footer />
      <Toast visible={toast.visible} message={toast.message} type={toast.type} />
    </div>
  );
}



